import {PageContent} from '@/components/organisms'
import {generateVideoThumbnailFromUrl, revokeThumbnailUrl} from '@/helpers/mediaThumbnail'
import {getPublicImageUrl} from '@/utils/commons'
import {useEffect, useState} from 'react'
import './_mediaGallery.scss'

const FALLBACK_IMAGE_URL = getPublicImageUrl('img/no-product-image-found.png')
const VIDEO_FILE_PATTERN = /\.(mp4|webm|ogg)(\?|#|$)/i

const inferIsVideoItem = item => {
  const mediaType = String(item?.mediaType || '').toUpperCase()
  const mediaUrl = item?.mediaUrl || ''
  const mediaTitle = item?.mediaTitle || ''
  const originalFileName = item?.originalFileName || ''

  if (mediaType.includes('VIDEO')) return true

  return [mediaUrl, mediaTitle, originalFileName].some(value => VIDEO_FILE_PATTERN.test(value))
}

const MediaGalleryItem = ({item}) => {
  const [displayUri, setDisplayUri] = useState(FALLBACK_IMAGE_URL)
  const [isLoading, setIsLoading] = useState(true)
  const isVideo = inferIsVideoItem(item)

  useEffect(() => {
    let isMounted = true
    let generatedThumbnailUri = ''

    setIsLoading(true)
    setDisplayUri(FALLBACK_IMAGE_URL)

    const createThumbnail = async () => {
      if (!isVideo || !item?.mediaUrl) {
        if (!item?.mediaUrl) {
          setDisplayUri(FALLBACK_IMAGE_URL)
          setIsLoading(false)
          return
        }

        if (isMounted) {
          setDisplayUri(item.mediaUrl)
          setIsLoading(false)
        }
        return
      }

      try {
        generatedThumbnailUri = await generateVideoThumbnailFromUrl(item.mediaUrl, {
          maxWidth: 468,
          maxHeight: 300,
          outputType: 'image/jpeg',
          quality: 0.85,
          captureAtSecond: 1,
        })

        if (isMounted) {
          setDisplayUri(generatedThumbnailUri)
          setIsLoading(false)
        }
      } catch {
        if (isMounted) {
          setDisplayUri(FALLBACK_IMAGE_URL)
          setIsLoading(false)
        }
      }
    }

    createThumbnail()

    return () => {
      isMounted = false
      revokeThumbnailUrl(generatedThumbnailUri)
    }
  }, [isVideo, item?.mediaUrl])

  return (
    <div className="flex flex-col gap-8">
      <div className="file-media relative flex h-[300px] items-center justify-center overflow-hidden rounded-lg border border-gray-600 p-3">
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-800/80">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-500 border-t-white" />
          </div>
        ) : null}
        <img
          src={displayUri}
          alt={item?.mediaTitle || 'Media'}
          className="h-full w-full rounded-lg object-contain"
          onError={event => {
            event.currentTarget.onerror = null
            event.currentTarget.src = FALLBACK_IMAGE_URL
            setIsLoading(false)
          }}
        />
      </div>
      {/* TODO add title back but for now it's removed */}
      {/* <div className="mt-2 w-full">
        <div className="media-title-row flex items-center border-b border-neutral-600">
          <span className="media-title-label">Title</span>
          <span className="media-title-filename">{item.mediaUrl.split('/').pop()}</span>
        </div>
      </div> */}
    </div>
  )
}

const MediaGallery = ({productData}) => {
  const getMediaItems = () => {
    return (productData || [])
      .filter(item => Boolean(item?.mediaUrl))
      .slice()
      .sort((a, b) => Number(a?.displayOrder || 0) - Number(b?.displayOrder || 0))
  }
  const mediaItems = getMediaItems()

  return (
    <>
      <PageContent className="media-gallery-component bg-neutral-800 text-white" pageContentWrapperClassName="relative">
        <div className="grid grid-cols-3 gap-6">
          {mediaItems.length === 0 ? (
            <div className="flex flex-col gap-8">
              <div className="file-media flex h-[300px] items-center justify-center overflow-hidden rounded-lg border border-gray-600 p-3">
                <img
                  src={FALLBACK_IMAGE_URL}
                  alt="no image"
                  className="h-full w-full rounded-lg object-contain"
                />
              </div>
              {/* <div className="mt-2 w-full">
                <div className="media-title-row flex items-center border-b border-neutral-600">
                  <span className="media-title-label">Title</span>
                  <span className="media-title-filename">No filename</span>
                </div>
              </div> */}
            </div>
          ) : (
            mediaItems.map(item => <MediaGalleryItem key={item.displayOrder} item={item} />)
          )}
        </div>
      </PageContent>
    </>
  )
}

export default MediaGallery
